import { Button, Col, Form, Row, Stack } from "react-bootstrap"
import CAccordion from "../../components/CAccordion"
import useUXStore from "../../api/ux/store"
import { ClientData, SESSION_TIMEOUT_CONSTANTS, DEVICE_LIMIT_CONSTANTS, LOGIN_RETRY_CONSTANTS } from "../../api/apps/data"
import { useState } from "react"
import { useApps } from "../../api/apps/store"
import Label from "../../components/Label"
import CDropdown from "../../components/CDropdown"
import TimeoutInput from "../../components/TimeoutInput"
import InputWithHelp from "../../components/InputWithHelp"
import HelpTooltip from "../../components/HelpTooltip"
import EditClientView from "./EditClientView"
import { SessionTimeoutValidation, TIMEOUT_HELP_TEXT, TimeUtils } from "../../utils/timeHelpers"
import { useAuth } from "../../api/auth/store"
import { SUPPORT_ROLE } from "../../constants"

const defaultClient: ClientData = { id: "asdfas", desc: "asdfasd", secret: "asd" }
const platforms = ["Web", "IOS", "Android", "Desktop"]

export const AddClientView = ({ app = "", onAdded = () => { } }) => {
    const [desc, setDesc] = useState("")
    const [redirect, setRedirect] = useState("")
    const [adding, setAdding] = useState(false)
    const [platform, setPlatform] = useState("Web")
    const [domain, setDomain] = useState("")
    const [customHash, setCustomHash] = useState(false)
    const [sessionActivityTimeout, setSessionActivityTimeout] = useState<number>(SESSION_TIMEOUT_CONSTANTS.DEFAULT_ACTIVITY_TIMEOUT)
    const [sessionTTL, setSessionTTL] = useState<number>(SESSION_TIMEOUT_CONSTANTS.DEFAULT_TTL)
    const [allowDelete, setAllowDelete] = useState(false)
    const [allowMultipleSessions, setAllowMultipleSessions] = useState(true)
    const [newSessionOnReauth, setNewSessionOnReauth] = useState(true)
    const [gen, setGen] = useState({ client_id: "", client_secret: "" })
    const [err, setErr] = useState({ name: "", redirect: "", domain: "", activityTimeout: "", ttl: "" })
    const { addClient } = useApps(state => state)
    const _onAdd = () => {
        setAdding(true)
        let chash = customHash
        if (platform === "Web") {
            setCustomHash(false)
            chash = false
        }

        // Validate timeout values
        const activityValidation = SessionTimeoutValidation.validateActivityTimeout(sessionActivityTimeout)
        const ttlValidation = SessionTimeoutValidation.validateTTL(sessionTTL)

        setErr({ name: "", redirect: "", domain: "", activityTimeout: "", ttl: "" })

        if (!activityValidation.isValid) {
            setErr(prev => ({ ...prev, activityTimeout: activityValidation.error! }))
            setAdding(false)
            return
        }

        if (!ttlValidation.isValid) {
            setErr(prev => ({ ...prev, ttl: ttlValidation.error! }))
            setAdding(false)
            return
        }

        addClient(app, {
            desc: desc,
            redirect: redirect,
            platform: platform,
            domain: domain,
            custom_hash: chash,
            session_activity_timeout: sessionActivityTimeout,
            session_ttl: sessionTTL,
            allow_delete: allowDelete,
            allow_multiple_sessions: allowMultipleSessions,
            new_session_on_reauth: newSessionOnReauth
        })
            .then(res => {
                switch (res.Code) {
                    case "200":
                        onAdded()
                        setGen(res.Msg)
                        break
                    case "incomplete_param":
                        setErr({ name: "Required", redirect: "", domain: "", activityTimeout: "", ttl: "" })
                        break;

                    default:
                        break;
                }
            })
            .catch(err => {

            })
            .finally(() => setAdding(false))
    }

    if (gen.client_id !== "") {
        return <Stack>
            <Row>
                <Col>
                    <Stack>
                        <Label margin="auto">Client Id</Label>
                        <Label margin="auto" fontWeight="400">{gen.client_id}</Label>
                    </Stack>
                </Col>
                <Col>
                    <Stack>
                        <Label margin="auto">Client Secret</Label>
                        <Label margin="auto" fontWeight="400">{gen.client_secret}</Label>
                    </Stack>
                </Col>
            </Row>
        </Stack>
    }
    return <Stack gap={2} style={{ padding: "10px" }}>
        <Row>
            <Col>
                <InputWithHelp
                    label="Name"
                    helpText="A descriptive name for this OAuth client application. This helps identify the client in the admin interface."
                    className="form-control modal-input"
                    errorMessage={err.name}
                    error={!!err.name}
                    disabled={adding}
                    onChange={(e: any) => setDesc(e.target.value)}
                    placeholder="Enter client name"
                />
            </Col>
        </Row>
        <Row>
            <Col>
                <InputWithHelp
                    label="Redirect URL"
                    helpText="The URL where users will be redirected after authentication. Must be a valid HTTPS URL for security."
                    className="form-control modal-input"
                    errorMessage={err.redirect}
                    error={!!err.redirect}
                    disabled={adding}
                    onChange={(e: any) => setRedirect(e.target.value)}
                    placeholder="https://your-app.com/callback"
                />
            </Col>
        </Row>
        <Row>
            <Col>
                <InputWithHelp
                    label="Domain"
                    helpText="Unique domain identifier for this client. Users will be exclusive to this domain, preventing conflicts between different applications. Usually this is company id."
                    className="form-control modal-input"
                    errorMessage={err.domain}
                    error={!!err.domain}
                    disabled={adding}
                    value={domain}
                    onChange={(e: any) => setDomain(e.target.value.replace(/[^a-zA-Z0-9._]/g, ''))}
                    placeholder="my-app-domain"
                />
            </Col>
        </Row>
        <Row>
            <Col style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ margin: 0, fontWeight: '500', fontSize: '14px' }}>Platform</label>
                <HelpTooltip content="The target platform for this client application. Different platforms have different security requirements." />
            </Col>
            <Col><CDropdown
                selected={platforms.findIndex(item => item === platform)}
                items={platforms}
                onSelected={(key, index) => {
                    setPlatform(key!)
                    setNewSessionOnReauth(true)

                }} /></Col>
        </Row>
        {platform !== "Web" && <Row>
            <Col style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ margin: 0, fontWeight: '500', fontSize: '14px' }}>Custom Hashing</label>
                <HelpTooltip content="Enable custom password hashing on the client side. Only available for non-Web platforms for enhanced security." />
            </Col>
            <Col>
                <Form.Check type="switch" checked={customHash} onChange={(e: any) => setCustomHash(e.target.checked)} />
            </Col>
        </Row>}
        <Row>
            <Col>
                <TimeoutInput
                    label="Session Activity Timeout"
                    helpText={TIMEOUT_HELP_TEXT.activityTimeout}
                    value={sessionActivityTimeout}
                    onChange={setSessionActivityTimeout}
                    error={err.activityTimeout}
                    disabled={adding}
                    placeholder="Enter activity timeout"
                />
            </Col>
        </Row>
        <Row>
            <Col>
                <TimeoutInput
                    label="Session TTL"
                    helpText={TIMEOUT_HELP_TEXT.ttl}
                    value={sessionTTL}
                    onChange={setSessionTTL}
                    error={err.ttl}
                    disabled={adding}
                    placeholder="Enter session TTL"
                />
            </Col>
        </Row>
        <Row>
            <Col style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ margin: 0, fontWeight: '500', fontSize: '14px' }}>Allow Account Deletion</label>
                <HelpTooltip content="Allow users from this client to delete their own accounts. This action is irreversible." />
            </Col>
            <Col>
                <Form.Check
                    type="switch"
                    checked={allowDelete}
                    disabled={adding}
                    onChange={(e: any) => setAllowDelete(e.target.checked)}
                />
            </Col>
        </Row>
        <Row>
            <Col style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ margin: 0, fontWeight: '500', fontSize: '14px' }}>Allow Multiple Sessions</label>
                <HelpTooltip content="Control whether users can have multiple active sessions. When disabled, logging in will terminate all other sessions for this app." />
            </Col>
            <Col>
                <Form.Check
                    type="switch"
                    checked={allowMultipleSessions}
                    disabled={adding}
                    onChange={(e: any) => setAllowMultipleSessions(e.target.checked)}
                />
            </Col>
        </Row>
        <Row>
            <Col style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ margin: 0, fontWeight: '500', fontSize: '14px' }}>New Session On Reauth</label>
                <HelpTooltip content="If enabled, reauthentication creates a new session ID and terminates the old one. Use this to prevent session ID reuse abuse or session fixation attacks." />
            </Col>
            <Col>
                <Form.Check
                    type="switch"
                    checked={newSessionOnReauth}
                    disabled={adding}
                    onChange={(e: any) => setNewSessionOnReauth(e.target.checked)}
                />
            </Col>
        </Row>
        <Row>
            <Col><Button className="btn blur-btn" disabled={adding} style={{ float: "right", marginTop: "10px" }} onClick={() => _onAdd()}>Add</Button></Col>
        </Row>
    </Stack>
}


const ClientItem = ({ client = defaultClient, onDelete = (client: ClientData) => { } }) => {
    const { refreshDialog } = useUXStore(state => state)
    const { hasRole } = useAuth(state => state)
    const [isEditing, setIsEditing] = useState(false)
    const editable = !hasRole(SUPPORT_ROLE)

    const onWillDelete = () => {
        refreshDialog({
            id: "deleteClient",
            visible: true,
            title: "Delete Client",
            content: <div>Are you sure you want to delete <b>{client.id}</b>? </div>,
            onConfirm() {
                onDelete(client)
            },
        }, true)
    }

    const handleEditComplete = () => {
        setIsEditing(false)
    }

    const handleEditCancel = () => {
        setIsEditing(false)
    }
    return <CAccordion label={client.desc}>
        {isEditing ? (
            <EditClientView
                client={client}
                onUpdated={handleEditComplete}
                onCancel={handleEditCancel}
            />
        ) : (
            <Stack gap={2}>
                <Row>
                    <Col>Name</Col>
                    <Col>{client.desc}</Col>
                </Row>
                <Row>
                    <Col>Client ID</Col>
                    <Col style={{ fontFamily: 'monospace', fontSize: '12px' }}>{client.id}</Col>
                </Row>
                <Row>
                    <Col>Secret</Col>
                    <Col style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {client.secret && client.secret.length >= 6
                            ? `${client.secret.substring(0, 3)}•••••••••••••••••••••••••••${client.secret.substring(client.secret.length - 3)}`
                            : '•••••••••••••••••••••••••••••••••'
                        }
                    </Col>
                </Row>
                <Row>
                    <Col>Redirect URL</Col>
                    <Col>{client.redirect}</Col>
                </Row>
                <Row>
                    <Col>Domain</Col>
                    <Col>{client.domain}</Col>
                </Row>
                <Row>
                    <Col>Platform</Col>
                    <Col>{client.platform}</Col>
                </Row>
                <Row>
                    <Col>Custom Hash</Col>
                    <Col>{client.custom_hash ? "Yes" : "No"}</Col>
                </Row>
                <Row>
                    <Col>Session Activity Timeout</Col>
                    <Col>{client.session_activity_timeout ? TimeUtils.secondsToHumanReadable(client.session_activity_timeout) : "Default"}</Col>
                </Row>
                <Row>
                    <Col>Session TTL</Col>
                    <Col>{client.session_ttl ? TimeUtils.secondsToHumanReadable(client.session_ttl) : "Default"}</Col>
                </Row>
                <Row>
                    <Col>Device Limit</Col>
                    <Col>{client.device_limit || DEVICE_LIMIT_CONSTANTS.DEFAULT}</Col>
                </Row>
                <Row>
                    <Col>Login Retry Limit</Col>
                    <Col>{client.login_retry_limit || LOGIN_RETRY_CONSTANTS.DEFAULT}</Col>
                </Row>
                <Row>
                    <Col>Allow Account Deletion</Col>
                    <Col>{client.allow_delete ? "Yes" : "No"}</Col>
                </Row>
                <Row>
                    <Col>Allow Multiple Sessions</Col>
                    <Col>{client.allow_multiple_sessions || client.allow_multiple_sessions === undefined ? "Yes" : "No"}</Col>
                </Row>
                <Row>
                    <Col>New Session On Reauth</Col>
                    <Col>{client.new_session_on_reauth ? "Yes" : "No"}</Col>
                </Row>
                <Row>
                    <Col>
                        {editable && <div style={{ display: 'flex', gap: '10px' }}>
                            <Button variant="outline-primary" onClick={() => setIsEditing(true)}>
                                Edit
                            </Button>
                            <Button variant="outline-danger" onClick={onWillDelete}>
                                Delete
                            </Button>
                        </div>}
                    </Col>
                </Row>
            </Stack>
        )}
    </CAccordion>
}

export default ClientItem